/**
 * BucketPlanSpotlight — Home "Today" strip for the Bucket List ladder.
 * 1) "Did you go?" for a plan whose date passed recently (one tap → Lived It).
 * 2) Otherwise "This weekend" for upcoming Sat/Sun plans.
 * Renders nothing when there is nothing to say (no empty states, no counts).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { CalendarHeart, MapPin, Sparkles, X, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGoalState } from "@/hooks/goal-state";
import { LivedItSheet } from "@/components/goals/LivedItSheet";
import { didYouGoCandidates, planDate, planLabel, weekendPlans } from "@/lib/bucketPlan";
import type { Goal } from "@/types/todo";

const DISMISS_KEY = "goaldigger.didyougo_dismissed";

const readDismissed = (): string[] => {
  try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]"); } catch { return []; }
};

export function BucketPlanSpotlight() {
  const { goals, handleUpdateGoal } = useGoalState();
  const [dismissed, setDismissed] = useState<string[]>(readDismissed);
  const [livedGoal, setLivedGoal] = useState<Goal | null>(null);

  // Dismissal is keyed by goal + planned date, so replanning asks again later.
  const keyOf = (g: Goal) => `${g.id}:${g.dueDate}`;
  const askGoal = useMemo(
    () => didYouGoCandidates(goals || []).find((g) => !dismissed.includes(keyOf(g))) ?? null,
    [goals, dismissed],
  );
  const weekend = useMemo(() => weekendPlans(goals || []), [goals]);

  const dismiss = (g: Goal) => {
    const next = [...dismissed, keyOf(g)].slice(-50);
    setDismissed(next);
    localStorage.setItem(DISMISS_KEY, JSON.stringify(next));
  };

  if (!askGoal && weekend.length === 0) return null;

  return (
    <>
      {askGoal ? (
        <div className="gd-surface relative overflow-hidden rounded-[28px] border-t border-white/40 dark:border-white/10 p-6">
          <div aria-hidden className="pointer-events-none absolute -top-20 -right-12 h-48 w-48 rounded-full bg-primary/15 blur-3xl" />
          <button
            type="button"
            onClick={() => dismiss(askGoal)}
            aria-label="Not now"
            className="absolute right-3 top-3 z-10 h-8 w-8 flex items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
            style={{ touchAction: "manipulation" }}
          >
            <X className="h-4 w-4" />
          </button>
          <div className="relative">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary/80 flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" /> Did you go?
            </p>
            <div className="mt-4 flex items-start gap-4">
              <div className="shrink-0 flex h-16 w-14 flex-col items-center justify-center rounded-2xl gd-well">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{format(planDate(askGoal)!, "MMM")}</span>
                <span className="font-outfit text-2xl font-bold leading-none text-foreground">{format(planDate(askGoal)!, "d")}</span>
              </div>
              <div className="min-w-0 pr-6">
                <p className="font-outfit text-lg font-bold tracking-tight text-foreground line-clamp-2">{askGoal.title}</p>
                <p className="gd-meta mt-1">Planned for {format(planDate(askGoal)!, "EEEE")}.</p>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <Button size="sm" className="gap-1.5 rounded-full active:scale-[0.98] transition-transform" onClick={() => setLivedGoal(askGoal)}>
                <Sparkles className="h-4 w-4" /> Yes, I lived it
              </Button>
              <Button size="sm" variant="outline" className="rounded-full" asChild>
                <Link to={`/goals/${askGoal.id}`}>Pick a new date</Link>
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="gd-surface relative overflow-hidden rounded-[28px] border-t border-white/40 dark:border-white/10 p-6">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary/80 flex items-center gap-1.5">
            <CalendarHeart className="h-3.5 w-3.5" /> This weekend
          </p>
          <h3 className="mt-2 font-outfit text-xl font-bold tracking-tight text-foreground">Your weekend dossier</h3>
          <ul className="mt-4 space-y-2.5">
            {weekend.slice(0, 2).map((g) => (
              <li key={g.id}>
                <Link
                  to={`/goals/${g.id}`}
                  className="group flex items-center gap-3 gd-well rounded-2xl p-3 transition duration-150 ease-out hover:-translate-y-0.5 active:scale-[0.98]"
                  style={{ touchAction: "manipulation" }}
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-outfit text-sm font-semibold text-foreground">{g.title}</span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{planLabel(planDate(g)!)}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </li>
            ))}
          </ul>
          {weekend.length > 2 && (
            <Link to="/goals?category=Bucket%20List" className="mt-3 inline-block text-xs font-semibold text-primary">
              See all weekend plans →
            </Link>
          )}
        </div>
      )}

      {livedGoal && (
        <LivedItSheet
          open={!!livedGoal}
          onOpenChange={(open) => !open && setLivedGoal(null)}
          goal={livedGoal}
          onUpdate={(updated) => {
            handleUpdateGoal(updated);
            setLivedGoal(null);
          }}
        />
      )}
    </>
  );
}
